import * as XLSX from 'xlsx-js-style';

export function exportAllPTSToExcel({
  years,
  currentYear,
  tekniikkaData = [],
  lviData = [],
  sahkoData = [],
  tutkimusData = [],
  tekniikkaYhteensa = [],
  lviYhteensa = [],
  sahkoYhteensa = [],
  tutkimusYhteensa = [],
  filename
}) {
  const GREEN = '04AA00';
  const LIGHT_GRAY = 'F0F0F0';
  const WHITE = 'FFFFFF';
  const BLACK = '000000';
  const RED = 'BA3B46';
  const YELLOW = 'D0C407';
  const DARK_GREEN = '04AA00';
  const BORDER = 'DDDDDD';

  const wb = XLSX.utils.book_new();

  const baseBorder = {
    top: { style: 'thin', color: { rgb: BORDER } },
    bottom: { style: 'thin', color: { rgb: BORDER } },
    left: { style: 'thin', color: { rgb: BORDER } },
    right: { style: 'thin', color: { rgb: BORDER } }
  };

  const greenHeaderStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const greenHeaderLeftStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const greenHeaderRightStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'right', vertical: 'center', wrapText: false },
    border: baseBorder
  };

  const sectionLabelStyle = {
    font: { bold: true, color: { rgb: BLACK } },
    fill: { fgColor: { rgb: LIGHT_GRAY } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const sectionFillStyle = {
    fill: { fgColor: { rgb: LIGHT_GRAY } },
    border: baseBorder
  };

  const labelStyle = {
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true, indent: 1 },
    border: baseBorder
  };

  const plainLabelStyle = {
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const textCenterStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    border: baseBorder
  };

  const numberStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const positiveValueStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const totalsStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const normalizeValue = (v) => {
    if (v === '' || v === null || v === undefined || v === '0' || v === 0) {
      return '';
    }
    const n = Number(v);
    return Number.isNaN(n) ? '' : n;
  };

  const getKLColor = (kl) => {
    switch (kl) {
      case 'KL1':
      case 'KL2':
        return RED;
      case 'KL3':
        return YELLOW;
      case 'KL4':
      case 'KL5':
        return DARK_GREEN;
      default:
        return BLACK;
    }
  };

  const calcSectionAverageKL = (items = []) => {
    const nums = items
      .map((it) => parseInt((it.kl || '').replace('KL', ''), 10))
      .filter((n) => !Number.isNaN(n));

    if (!nums.length) return '';
    return `KL${Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)}`;
  };

  const styleRow = (ws, rowIndex, colCount, styleFactory) => {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
      if (!ws[addr]) continue;
      ws[addr].s = typeof styleFactory === 'function' ? styleFactory(c, ws[addr]) : styleFactory;
    }
  };

  const styleCell = (ws, rowIndex, colIndex, style) => {
    const addr = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
    if (ws[addr]) ws[addr].s = style;
  };

  const makeRangeSumFormula = (colIndexZeroBased, startRowZeroBased, endRowZeroBased) => {
    if (endRowZeroBased < startRowZeroBased) return '';
    const colLetter = XLSX.utils.encode_col(colIndexZeroBased);
    return { t: 'n', f: `SUM(${colLetter}${startRowZeroBased + 1}:${colLetter}${endRowZeroBased + 1})` };
  };

  const makeRowSumFormula = (rowIndexZeroBased, startColZeroBased, endColZeroBased) => {
    if (endColZeroBased < startColZeroBased) return '';
    const startRef = `${XLSX.utils.encode_col(startColZeroBased)}${rowIndexZeroBased + 1}`;
    const endRef = `${XLSX.utils.encode_col(endColZeroBased)}${rowIndexZeroBased + 1}`;
    return { t: 'n', f: `SUM(${startRef}:${endRef})` };
  };

  const createTekniikkaLikeSheet = ({ sheetName, type, tableData }) => {
    const colCount = 2 + years.length;
    const rows = [];
    const sectionRows = [];
    const itemRows = [];

    rows.push([
      '',
      '',
      `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
      ...Array(years.length - 1).fill('')
    ]);

    rows.push([type, '', ...years.map(String)]);

    (tableData || []).forEach((section) => {
      const avgKL = calcSectionAverageKL(section.items);
      const sectionRowIndex = rows.length;
      sectionRows.push(sectionRowIndex);

      rows.push([section.name, avgKL, ...Array(years.length).fill('')]);

      (section.items || []).forEach((item) => {
        const label = (item.label || '').trim();
        if (label.length < 1) return;

        const values = (item.values || []).map(normalizeValue);
        const rowIndex = rows.length;

        itemRows.push({ rowIndex, values });

        rows.push([
          label,
          '',
          ...values
        ]);
      });
    });

    const totalRowIdx = rows.length;
    const totalsRow = ['YHTEENSÄ', ''];

    const firstItemRow = itemRows.length ? itemRows[0].rowIndex : -1;
    const lastItemRow = itemRows.length ? itemRows[itemRows.length - 1].rowIndex : -1;

    for (let yearIdx = 0; yearIdx < years.length; yearIdx++) {
      totalsRow.push(
        itemRows.length
          ? makeRangeSumFormula(yearIdx + 2, firstItemRow, lastItemRow)
          : ''
      );
    }

    rows.push(totalsRow);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws['!merges'] = [
      { s: { r: 0, c: 2 }, e: { r: 0, c: colCount - 1 } }
    ];

    ws['!cols'] = [
      { wch: 42 },
      { wch: 7 },
      ...years.map(() => ({ wch: 9 }))
    ];

    ws['!rows'] = rows.map((_, i) => (i <= 1 ? { hpt: 20 } : { hpt: 18 }));

    styleRow(ws, 0, colCount, (c) => (c >= 2 ? greenHeaderRightStyle : greenHeaderStyle));
    styleRow(ws, 1, colCount, greenHeaderStyle);

    sectionRows.forEach((r) => {
      styleRow(ws, r, colCount, (c, cell) => {
        if (c === 0) return sectionLabelStyle;
        if (c === 1) {
          return {
            font: { bold: true, color: { rgb: getKLColor(cell.v || '') } },
            fill: { fgColor: { rgb: LIGHT_GRAY } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: baseBorder
          };
        }
        return sectionFillStyle;
      });
    });

    itemRows.forEach(({ rowIndex, values }) => {
      styleCell(ws, rowIndex, 0, labelStyle);
      styleCell(ws, rowIndex, 1, textCenterStyle);

      values.forEach((v, idx) => {
        styleCell(ws, rowIndex, idx + 2, v !== '' && Number(v) > 0 ? positiveValueStyle : numberStyle);
      });
    });

    styleRow(ws, totalRowIdx, colCount, totalsStyle);

    XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  };

  const createTutkimustarpeetSheet = () => {
    const tableData = tutkimusData || [];
    const colCount = 1 + years.length;
    const rows = [];
    const itemRows = [];

    rows.push([
      `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
      ...Array(colCount - 1).fill('')
    ]);

    rows.push([
      'Tutkimustarpeet',
      ...years.map(String)
    ]);

    tableData.forEach((section) => {
      (section.items || []).forEach((item) => {
        const label = (item.label || '').trim();
        if (label.length < 1) return;

        const values = (item.values || []).map(normalizeValue);
        const rowIndex = rows.length;
        itemRows.push({ rowIndex, values });

        rows.push([label, ...values]);
      });
    });

    const totalRowIdx = rows.length;
    const totalRow = ['YHTEENSÄ'];

    for (let yearIdx = 0; yearIdx < years.length; yearIdx++) {
      totalRow.push(
        makeRangeSumFormula(
          yearIdx + 1,
          2,
          totalRowIdx - 1
        )
      );
    }

    rows.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }
    ];

    ws['!cols'] = [
      { wch: 42 },
      ...years.map(() => ({ wch: 9 }))
    ];

    ws['!rows'] = rows.map((_, i) => (i <= 1 ? { hpt: 20 } : { hpt: 18 }));

    styleRow(ws, 0, colCount, greenHeaderRightStyle);
    styleRow(ws, 1, colCount, greenHeaderStyle);

    itemRows.forEach(({ rowIndex, values }) => {
      styleCell(ws, rowIndex, 0, plainLabelStyle);

      values.forEach((v, idx) => {
        styleCell(ws, rowIndex, idx + 1, v !== '' && Number(v) > 0 ? positiveValueStyle : numberStyle);
      });
    });

    styleRow(ws, totalRowIdx, colCount, totalsStyle);

    XLSX.utils.book_append_sheet(wb, ws, 'Tutkimustarpeet');
  };

  const createYhteenvetoSheet = () => {
    const colCount = 2 + years.length;
    const rows = [];
    const bodyRows = [];

    const summaryRows = [
      { label: 'Lisätutkimukset', values: tutkimusYhteensa || [] },
      { label: 'Rakennetekniikka', values: tekniikkaYhteensa || [] },
      { label: 'LVI Järjestelmät', values: lviYhteensa || [] },
      { label: 'Sähköjärjestelmät', values: sahkoYhteensa || [] }
    ];

    rows.push([
      'Toimenpide-ehdotukset yhteensä',
      `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
      ...Array(years.length).fill('')
    ]);

    rows.push([
      'Osa-alue',
      'Yhteensä',
      ...years.map(String)
    ]);

    summaryRows.forEach((row) => {
      const values = row.values.map(normalizeValue);
      const rowIndex = rows.length;

      bodyRows.push({ rowIndex, values });

      rows.push([
        row.label,
        makeRowSumFormula(rowIndex, 2, years.length + 1),
        ...values
      ]);
    });

    const totalRowIdx = rows.length;
    const footerRow = ['YHTEENSÄ'];

    footerRow.push(
      makeRangeSumFormula(1, 2, totalRowIdx - 1)
    );

    for (let yearIdx = 0; yearIdx < years.length; yearIdx++) {
      footerRow.push(
        makeRangeSumFormula(yearIdx + 2, 2, totalRowIdx - 1)
      );
    }

    rows.push(footerRow);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws['!merges'] = [
      { s: { r: 0, c: 1 }, e: { r: 0, c: colCount - 1 } }
    ];

    ws['!cols'] = [
      { wch: 28 },
      { wch: 12 },
      ...years.map(() => ({ wch: 9 }))
    ];

    ws['!rows'] = rows.map((_, i) => (i <= 1 ? { hpt: 20 } : { hpt: 18 }));

    styleRow(ws, 0, colCount, (c) => (c === 0 ? greenHeaderLeftStyle : greenHeaderRightStyle));
    styleRow(ws, 1, colCount, greenHeaderStyle);

    bodyRows.forEach(({ rowIndex, values }) => {
      styleCell(ws, rowIndex, 0, plainLabelStyle);

      const totalCellValue = ws[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })];
      if (totalCellValue) {
        const hasPositive = values.some(v => v !== '' && Number(v) > 0);
        totalCellValue.s = hasPositive ? positiveValueStyle : numberStyle;
      }

      values.forEach((v, idx) => {
        styleCell(ws, rowIndex, idx + 2, v !== '' && Number(v) > 0 ? positiveValueStyle : numberStyle);
      });
    });

    styleRow(ws, totalRowIdx, colCount, totalsStyle);

    XLSX.utils.book_append_sheet(wb, ws, 'Yhteenveto');
  };

  createYhteenvetoSheet();
  createTutkimustarpeetSheet();

  createTekniikkaLikeSheet({
    sheetName: 'Rakennetekniikka',
    type: 'Rakennetekniikka',
    tableData: tekniikkaData
  });

  createTekniikkaLikeSheet({
    sheetName: 'LVI Järjestelmät',
    type: 'LVI Järjestelmät',
    tableData: lviData
  });

  createTekniikkaLikeSheet({
    sheetName: 'Sähköjärjestelmät',
    type: 'Sähköjärjestelmät',
    tableData: sahkoData
  });

  const outFilename = filename || `PTS_kaikki_${currentYear}.xlsx`;
  XLSX.writeFile(wb, outFilename);
}